
import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '@/common/api.service';
import { useSessionsStore } from './sessions';
import { useResourceStore } from './resource';

export const useCoursesStore = defineStore('courses', () => {
  // State
  const courses = ref([]);
  const course = ref(null);

  // Getters
  const coursesChapter = computed(() => {
    const chapters = [];
    courses.value.forEach((c) => {
      let index = c.chapterBasicOrder;
      if (index === null) {
        index = 0;
      }
      if (!chapters[index]) {
        chapters[index] = [];
      }
      chapters[index].push(c);
    });
    return chapters.filter((x) => x !== undefined);
  });

  // Actions
  function setCourses(payload = {}) {
    const data = payload.data || payload;
    courses.value = Array.isArray(data?.courses) ? data.courses : [];
  }

  function setCurrentCourse(c) {
    course.value = c;
  }

  async function fetchCourses() {
    const sessionsStore = useSessionsStore();
    const resourceStore = useResourceStore();
    const { currentSession } = sessionsStore;

    if (!currentSession) throw new Error("Current session not available");

    const { data } = await ApiService.get(`front/v1/${currentSession.id}/courses`);
    setCourses(data);
    resourceStore.setResources(data); // Assuming resources are part of courses response
    sessionsStore.setCurrentSessionProgress(data.data.progress);
    sessionsStore.setCurrentSessionActivatedBadges(data.data.activatedBadges);

    if (!currentSession.attendee && data.data.attendeeId) {
      const updatedSession = {
        ...currentSession,
        attendee: data.data.attendeeId,
      };
      sessionsStore.setCurrentSession(updatedSession);
    }
    return data;
  }

  async function selectCourse(courseId) {
    const normalizedCourseId = Number(courseId);
    const oldCourseId = course.value ? course.value.id : null;
    if (!normalizedCourseId) {
      setCurrentCourse(null);
      return;
    }
    const acceptNotAvailable = courses.value.filter((x) => x.available).length === 0;
    const foundCourse = courses.value.find(
      (courseObject) => Number(courseObject.id) === normalizedCourseId
        && ((acceptNotAvailable || normalizedCourseId === oldCourseId) ? true : courseObject.available),
    );

    if (foundCourse) {
      foundCourse.keepActive = foundCourse.fileType === 'VTS' && normalizedCourseId === oldCourseId;
      setCurrentCourse(foundCourse);
    } else {
      throw new Error('Course not found or not available');
    }
  }

  async function setCourse(courseId) {
    return selectCourse(courseId);
  }

  return {
    courses,
    course,
    coursesChapter,
    setCourses,
    setCurrentCourse,
    fetchCourses,
    selectCourse,
    setCourse,
  };
});
